import { Trans, t } from '@lingui/macro'
import { Item } from '@react-stately/collections'
import cx from 'classnames'
import * as React from 'react'

import { stripMaybes } from '@emico/utils'

import { useSizeTables } from './getSizeTables.query'
import { MeasurementsInfo } from './MeasurementsInfo'
import styles from './SizeGuide.module.scss'
import { SizeTable, SizeTableUrl } from '../../graphql/schema.generated'
import Button from '../../input/Button'
import PopperSelect from '../../input/PopperSelect'
import Heading from '../../typography/Heading'
import Loader from '../Loader'
import ResponsiveModal from '../ResponsiveModal/ResponsiveModal'

interface Props {
    close(): void
    sizeTableIdentifier?: string
}

const generateRow = (data: string[] | undefined) => {
    if (!data) {
        return null
    }

    const heading = data.shift()
    const flexBasis = Math.round(100 / data.length)

    return (
        <div className={styles.row}>
            <div className={cx(styles.rowHeading)}>{heading}</div>
            <div className={styles.cellWrapper}>
                {data.map((value, index) => (
                    <div
                        className={cx(styles.cell)}
                        key={`${index}-${value}`}
                        style={{ flexBasis }}
                    >
                        <span>{value}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

type SizeTableParsed = {
    descr: string
    uid: string
    name: string
    cells: React.ReactNode | JSX.Element
    urls?: SizeTableUrl[]
}

const parseSizeTable = (
    sizeTable: SizeTable,
): Array<JSX.Element | React.ReactNode> => {
    const { rows } = sizeTable

    return rows.reduce<Array<JSX.Element | React.ReactNode | null>>(
        (memo, curr) => [
            ...memo,
            generateRow(
                curr?.cells
                    ?.filter(stripMaybes)
                    .map((cell) => cell?.content ?? ''),
            ),
        ],
        [],
    )
}

const parseSizeTables = (sizeTables: SizeTable[]) =>
    sizeTables.reduce<SizeTableParsed[]>(
        (memo, curr) => [
            ...memo,
            {
                descr: curr.description ?? '',
                name: curr.name ?? '',
                cells: parseSizeTable(curr),
                uid: curr?.uid,
                urls: curr?.urls?.filter(stripMaybes),
            },
        ],
        [],
    )

const SizeGuideModal = ({ close, sizeTableIdentifier }: Props) => {
    const { data: sizeTables, loading, uids } = useSizeTables()
    const gridRef = React.useRef<HTMLDivElement>(null)
    const [selectedId, setSelectedId] = React.useState<string>(
        sizeTableIdentifier ?? '',
    )

    const tables = parseSizeTables(sizeTables)
    const sizes = (
        <>
            {tables?.map((table) => {
                const textUrls =
                    table.urls?.filter(
                        (url) => url.type?.toLowerCase() === 'text',
                    ) ?? []
                const buttonUrls =
                    table.urls?.filter(
                        (url) => url.type?.toLowerCase() === 'button',
                    ) ?? []

                return (
                    <div
                        key={table.uid}
                        data-uid={table.uid}
                        className={styles.sizeTableWrapper}
                    >
                        <div className={cx(styles.sizeTableCtas, styles.top)}>
                            <Heading variant="h3" element="h3">
                                {table?.name}
                            </Heading>
                            {textUrls?.length > 0 &&
                                textUrls?.map((textUrl) => (
                                    <Button
                                        key={textUrl.label}
                                        variant="linkInverted"
                                        name={textUrl.label}
                                        category="sizeTableCta"
                                        to={textUrl.url}
                                    >
                                        {textUrl.label}
                                    </Button>
                                ))}
                        </div>
                        <div className={styles.sizeTable}>{table.cells}</div>

                        {buttonUrls?.length > 0 && (
                            <div className={styles.sizeTableCtas}>
                                {buttonUrls.map((btnUrl) => (
                                    <Button
                                        onClick={close}
                                        key={btnUrl.label}
                                        variant="primary"
                                        name={btnUrl.label}
                                        category="sizeTableCta"
                                        to={btnUrl.url}
                                    >
                                        {btnUrl.label}
                                    </Button>
                                ))}
                            </div>
                        )}
                    </div>
                )
            })}
        </>
    )

    const handleChange = (chartUid: any) => {
        if (gridRef.current) {
            const target = Array.from(gridRef.current?.children).find(
                (elem) => elem.getAttribute('data-uid') === chartUid,
            )
            if (target) {
                target.scrollIntoView({ behavior: 'smooth' })
                setTimeout(() => {
                    setSelectedId('')
                }, 0)
            }
        }
    }

    React.useLayoutEffect(() => {
        if (!loading && sizeTables.length && selectedId) {
            setTimeout(() => {
                handleChange(selectedId)
            }, 1)
        }
    }, [selectedId, sizeTables.length, loading])

    return (
        <ResponsiveModal
            className={cx(styles.sizeTableModal, styles.scrollLock)}
            onBack={close}
            header={
                <>
                    <Heading variant="h2" element="h2" color="dark">
                        <Trans id="catalog.productDetailView.sizeGuideHeading">
                            Size guide
                        </Trans>
                    </Heading>
                    <div>
                        <div className={styles.headerDescription}>
                            <Trans id="catalog.productDetailView.sizeGuideDescription">
                                The dimensions on the size chart are body
                                measurements in cm. Find your correct size in
                                the table below. Scroll down to see more sizes.
                            </Trans>
                        </div>
                        <div className={styles.actions}>
                            <PopperSelect
                                selectAnOptionLabel={t({
                                    id: 'catalog.productDetailView.sizeGuideSelectAnOption',
                                    message: `Select a product group`,
                                })}
                                selectedKey={selectedId}
                                className={styles.chartSelector}
                                items={uids}
                                onSelectionChange={(key) =>
                                    setSelectedId(key.toString())
                                }
                            >
                                {(item) => (
                                    <Item key={item.uid}>{item.name}</Item>
                                )}
                            </PopperSelect>
                            <Button
                                variant="secondaryAlt"
                                category="sizeGuide"
                                name="scrollToMeasurements"
                                onClick={() => setSelectedId('measurements')}
                                className={styles.measurementsButton}
                            >
                                <Trans id="catalog.productDetailView.scrollToMeasurements">
                                    Meetinstructies
                                </Trans>
                            </Button>
                        </div>
                    </div>
                </>
            }
        >
            {loading ? (
                <Loader />
            ) : (
                <>
                    <div ref={gridRef} className={styles.grid}>
                        {sizes}
                        <MeasurementsInfo />
                    </div>
                </>
            )}
        </ResponsiveModal>
    )
}

export default SizeGuideModal
